import { useState, type CSSProperties, type KeyboardEvent } from "react";
import { NodeAvatar } from "../primitives";
import { getAgentPersona } from "../cockpit/CockpitHeader";
import { FocusBlock, deriveFocusState } from "./FocusBlock";
import { useAgentBrainSnapshot, useNeedsYou } from "../../hooks/use-focus-model";
import type { AgentStatus, LibraryNode } from "../../../lib/types";

export interface AgentCardProps {
  agent: LibraryNode;
  status: AgentStatus | undefined;
  onOpen: (nodeId: string) => void;
}

export function AgentCard({ agent, status, onOpen }: AgentCardProps) {
  const [hovered, setHovered] = useState(false);
  const persona = getAgentPersona({ path: agent.path, agent_config: agent.agent_config });

  const snapshotQuery = useAgentBrainSnapshot(agent.id);
  const focus = snapshotQuery.data?.current ?? null;

  const needsYouQuery = useNeedsYou(agent.id);
  const needsYouCount = needsYouQuery.data?.length ?? 0;

  const focusState = deriveFocusState(focus, needsYouCount);
  const role = agent.description?.trim() || "Agent";

  const card: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    padding: "14px 16px",
    background: "var(--color-background-primary)",
    border: "0.5px solid var(--color-border-tertiary)",
    borderRadius: "var(--border-radius-lg)",
    borderColor: hovered ? "var(--color-border-secondary)" : undefined,
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
    textAlign: "left",
    width: "100%",
    transition: "border-color 120ms ease",
    minWidth: 0,
  };

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen(agent.id);
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${agent.name} — ${role}`}
      style={card}
      onClick={() => onOpen(agent.id)}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, minWidth: 0 }}>
        <NodeAvatar type="agent" persona={persona} name={agent.name} size="lg" />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: "var(--color-text-primary)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={agent.name}
          >
            {agent.name}
          </div>
          <div
            style={{
              fontSize: 12,
              color: "var(--color-text-secondary)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={role}
          >
            {role}
          </div>
        </div>
      </div>

      {/* Focus block */}
      <FocusBlock
        variant="workforce-card"
        focus={focus}
        focusState={focusState}
        durationSeconds={status?.duration_seconds ?? null}
      />
    </div>
  );
}
